import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { cmsService } from '@services/api'
import {
  // MapPin,
  Plus,
  Search,
  // Filter,
  Download,
  Eye,
  // Edit,
  // Trash2,
  // Clock,
  Users,
  Calendar,
  Navigation,
  CheckCircle,
  XCircle,
  AlertCircle,
  // Play,
  // Pause,
  Route,
  Truck
} from 'lucide-react'
import {
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Card,
  CardBody,
  CardHeader,
  Center,
  Flex,
  // useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  SimpleGrid,
  Box,
  // Avatar,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  useToast,
  Tooltip,
  Spinner,
  Avatar,
  // Alert,
  // AlertIcon,
  // AlertTitle,
  // AlertDescription,
} from '@chakra-ui/react'

// Types for trip data
interface Trip {
  id: string;
  daladalaId: string;
  routeId: string;
  timetableId?: string;
  scheduledDeparture?: string;
  actualDeparture?: string;
  scheduledArrival?: string;
  actualArrival?: string;
  totalBookings: number;
  availableSeats?: number;
  status: 'scheduled' | 'boarding' | 'in_transit' | 'completed' | 'cancelled';
  createdAt: string;
  updatedAt: string;
  daladala?: {
    id: string;
    registrationNumber: string;
    totalSeats: number;
    seatLayout?: any;
    driver?: {
      id: string;
      firstName: string;
      lastName: string;
      phoneNumber: string;
      email?: string;
    };
    owner?: {
      id: string;
      firstName: string;
      lastName: string;
      phoneNumber: string;
      email?: string;
    };
    conductor?: {
      id: string;
      firstName: string;
      lastName: string;
      phoneNumber: string;
    };
  };
  route?: {
    id: string;
    name: string;
    startPoint: string;
    endPoint: string;
    routeStops?: Array<{
      stop: { name: string };
      stopOrder: number;
    }>;
  };
  bookings?: any[];
}

interface TripFormData {
  daladalaId: string
  routeId: string
  timetableId?: string
  scheduledDeparture: string
  scheduledArrival: string
}

const getErrorMessage = (error: any, fallback: string) => {
  if (!error) return fallback
  const apiError = error?.response?.data?.error
  if (!apiError) return error?.message || fallback
  if (typeof apiError === 'string') return apiError
  if (typeof apiError?.message === 'string') return apiError.message
  try {
    return JSON.stringify(apiError)
  } catch {
    return fallback
  }
}

function TripFormModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [formData, setFormData] = useState<TripFormData>({
    daladalaId: '',
    routeId: '',
    timetableId: '',
    scheduledDeparture: '',
    scheduledArrival: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const toast = useToast()
  const queryClient = useQueryClient()

  // Fetch available daladalas and routes for the form
  const { data: daladalasData } = useQuery('vehicles', () => cmsService.getVehicles({ limit: 100 }))
  const { data: routesData } = useQuery('routes', () => cmsService.getAllRoutesForCMS())

  const createTripMutation = useMutation((tripData: any) => cmsService.createTrip(tripData))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.daladalaId || !formData.routeId || !formData.scheduledDeparture || !formData.scheduledArrival) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in all required fields before creating a trip.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
      return
    }

    setIsLoading(true)

    try {
      const payload: TripFormData = {
        ...formData,
        timetableId: formData.timetableId?.trim() ? formData.timetableId.trim() : undefined
      }
      if (!payload.timetableId) {
        delete payload.timetableId
      }

      await createTripMutation.mutateAsync(payload)
      await queryClient.invalidateQueries('trips')
      await queryClient.invalidateQueries('dashboard-stats')
      toast({
        title: 'Trip Created',
        description: 'Trip has been successfully created.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      setFormData({
        daladalaId: '',
        routeId: '',
        timetableId: '',
        scheduledDeparture: '',
        scheduledArrival: ''
      })
      onClose()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: getErrorMessage(error, 'Failed to create trip. Please try again.'),
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const daladalas = daladalasData?.data || []
  const routes = routesData?.data || []

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(5px)" />
      <ModalContent borderRadius="2xl">
        <ModalHeader borderBottom="1px solid" borderColor="gray.100">Create New Trip</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6} pt={6}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={5}>
              <FormControl isRequired>
                <FormLabel fontWeight="semibold">Daladala Vehicle</FormLabel>
                <Select
                  value={formData.daladalaId}
                  onChange={(e) => setFormData({ ...formData, daladalaId: e.target.value })}
                  placeholder="Select a daladala"
                  borderRadius="xl"
                >
                  {daladalas.map((daladala: any) => (
                    <option key={daladala.id} value={daladala.id}>
                      {daladala.registrationNumber} - {daladala.driver ? `${daladala.driver.firstName} ${daladala.driver.lastName}` : 'No Driver'}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontWeight="semibold">Route</FormLabel>
                <Select
                  value={formData.routeId}
                  onChange={(e) => setFormData({ ...formData, routeId: e.target.value })}
                  placeholder="Select a route"
                  borderRadius="xl"
                >
                  {routes.map((route: any) => (
                    <option key={route.id} value={route.id}>
                      {route.name} ({route.startPoint} → {route.endPoint})
                    </option>
                  ))}
                </Select>
              </FormControl>

              <SimpleGrid columns={2} spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel fontWeight="semibold">Scheduled Departure</FormLabel>
                  <Input
                    type="datetime-local"
                    value={formData.scheduledDeparture}
                    onChange={(e) => setFormData({ ...formData, scheduledDeparture: e.target.value })}
                    borderRadius="xl"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontWeight="semibold">Scheduled Arrival</FormLabel>
                  <Input
                    type="datetime-local"
                    value={formData.scheduledArrival}
                    onChange={(e) => setFormData({ ...formData, scheduledArrival: e.target.value })}
                    borderRadius="xl"
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel fontWeight="semibold">Timetable (Optional)</FormLabel>
                <Input
                  value={formData.timetableId}
                  onChange={(e) => setFormData({ ...formData, timetableId: e.target.value })}
                  placeholder="Reference Timetable ID"
                  borderRadius="xl"
                />
              </FormControl>

              <Button
                mt={2}
                colorScheme="brand"
                isLoading={isLoading}
                type="submit"
                w="full"
                size="lg"
                borderRadius="xl"
              >
                Create Trip
              </Button>
            </VStack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

// ── Interactive Bus Seat Map (Read-Only) ──────────────────────────────────
function BusSeatMap({
  capacity,
  layoutType,
  layoutRows,
  lastRowFull,
  reservedSeats,
  bookedSeats,
}: {
  capacity: number
  layoutType: string
  layoutRows: number
  lastRowFull: boolean
  reservedSeats: number[]
  bookedSeats: Record<number, any>
}) {
  const leftCols = layoutType === '2+2' ? 2 : 1
  const rightCols = 2
  const baseCols = leftCols + rightCols

  let rows = layoutRows
  if (rows === 0) {
    const middleNumberedNeeded = Math.max(capacity - 5, 0)
    rows = Math.ceil(middleNumberedNeeded / baseCols) + 1
  }

  const numberedStart = 2
  const numberedCount = capacity
  const SEAT_SZ = '32px'

  type GridCell = { n: number; kind: 'seat' } | { letter: string; kind: 'middle' } | { kind: 'empty' }
  const grid: GridCell[][] = []
  let numberedSeat = numberedStart
  let midLetter = 0

  for (let r = 0; r < rows; r++) {
    const rowNum = r + 1
    const isLast = r === rows - 1
    const hasMiddleToggle = false // read only
    const fillAisleWithSeat = isLast && layoutType === '2+1'
    const row: GridCell[] = []
    
    for (let c = 0; c < leftCols; c++) {
      if (numberedSeat <= numberedCount) row.push({ n: numberedSeat++, kind: 'seat' })
      else row.push({ kind: 'empty' })
    }
    
    if (fillAisleWithSeat) {
      if (numberedSeat <= numberedCount) row.push({ n: numberedSeat++, kind: 'seat' })
      else row.push({ kind: 'empty' })
    } else if (hasMiddleToggle) {
      row.push({ letter: String.fromCharCode(65 + midLetter++), kind: 'middle' })
    } else {
      row.push({ kind: 'empty' })
    }

    for (let c = 0; c < rightCols; c++) {
      if (numberedSeat <= numberedCount) row.push({ n: numberedSeat++, kind: 'seat' })
      else row.push({ kind: 'empty' })
    }
    grid.push(row)
  }

  const SeatCell = ({ cell }: { cell: GridCell }) => {
    if (cell.kind === 'empty') return <Box w={SEAT_SZ} h={SEAT_SZ} />
    if (cell.kind === 'middle') {
      return (
        <Box w={SEAT_SZ} h={SEAT_SZ} bg="white" border="1px solid" borderColor="gray.300" borderRadius="6px" display="flex" alignItems="center" justifyContent="center">
          <Text fontSize="10px" fontWeight="bold" color="gray.600">{cell.letter}</Text>
        </Box>
      )
    }

    const isReserved = reservedSeats.includes(cell.n)
    const booking = bookedSeats[cell.n]
    const isBooked = !!booking

    let bgColor = 'gray.100'
    let borderColor = 'gray.300'
    let color = 'gray.600'
    let tooltip = `Seat ${cell.n}: Available`

    if (isReserved) {
      bgColor = 'red.100'
      borderColor = 'red.400'
      color = 'red.700'
      tooltip = `Seat ${cell.n}: Reserved/Damaged`
    } else if (isBooked) {
      bgColor = 'brand.100'
      borderColor = 'brand.400'
      color = 'brand.700'
      tooltip = `Booked to: ${booking.passengerName || booking.user?.firstName || 'Passenger'}`
    }

    return (
      <Tooltip label={tooltip} hasArrow>
        <Box
          w={SEAT_SZ} h={SEAT_SZ}
          bg={bgColor} border="2px solid" borderColor={borderColor} color={color}
          borderRadius="8px" display="flex" alignItems="center" justifyContent="center"
          boxShadow={isBooked ? "sm" : "none"}
        >
          <Text fontSize="10px" fontWeight="bold">{cell.n}</Text>
        </Box>
      </Tooltip>
    )
  }

  return (
    <Box p={4} bg="gray.50" borderRadius="20px" border="1px solid" borderColor="gray.200" boxShadow="sm">
      <VStack spacing={2} align="center">
        {/* Front */}
        <HStack w="full" justify="space-between" mb={2} px={4}>
          <SeatCell cell={{ n: 1, kind: 'seat' }} />
          <Box flex={1} />
          <Box w={SEAT_SZ} h={SEAT_SZ} bg="red.50" border="1px solid" borderColor="red.200" borderRadius="8px" display="flex" alignItems="center" justifyContent="center">
            <Text fontSize="10px" color="red.600" fontWeight="bold">D</Text>
          </Box>
        </HStack>
        
        {/* Rows */}
        <VStack spacing={1} align="center">
          {grid.map((row, rIdx) => (
            <HStack key={rIdx} spacing={1} justify="center">
              {row.map((cell, i) => <SeatCell key={i} cell={cell} />)}
            </HStack>
          ))}
        </VStack>

        <HStack mt={4} spacing={4} fontSize="10px" fontWeight="medium">
           <HStack spacing={1}><Box w="10px" h="10px" bg="gray.100" border="1px solid" borderColor="gray.300" borderRadius="2px"/><Text>Available</Text></HStack>
           <HStack spacing={1}><Box w="10px" h="10px" bg="brand.100" border="1px solid" borderColor="brand.400" borderRadius="2px"/><Text>Booked</Text></HStack>
           <HStack spacing={1}><Box w="10px" h="10px" bg="red.100" border="1px solid" borderColor="red.400" borderRadius="2px"/><Text>Reserved</Text></HStack>
        </HStack>
      </VStack>
    </Box>
  )
}

// ── Trip Comprehensive View Modal ───────────────────────────────────────
function TripViewModal({ isOpen, onClose, tripId }: { isOpen: boolean; onClose: () => void; tripId: string | null }) {
  const { data: tripData, isLoading } = useQuery(
    ['trip-detail', tripId],
    () => cmsService.getTripById(tripId!),
    { enabled: !!tripId, retry: false }
  )

  const trip: Trip | null = tripData?.data || null

  if (!trip && !isLoading) return null

  // Map booked seats
  const bookedSeats: Record<number, any> = {}
  if (trip?.bookings) {
    trip.bookings.forEach(booking => {
      if (booking.seatNumbers) {
        let seats: any[] = []
        if (typeof booking.seatNumbers === 'string') {
          try { seats = JSON.parse(booking.seatNumbers) } catch { seats = [booking.seatNumbers] }
        } else if (Array.isArray(booking.seatNumbers)) {
          seats = booking.seatNumbers
        }
        
        seats.forEach(s => {
          const sNum = parseInt(s)
          if (!isNaN(sNum)) bookedSeats[sNum] = booking
        })
      }
    });
  }

  const seatLayout = trip?.daladala?.seatLayout || { layoutType: '2+1', rows: 0, lastRowFull: true, reservedSeats: [] }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(8px)" />
      <ModalContent borderRadius="3xl" m={4}>
        <ModalHeader borderBottom="1px solid" borderColor="gray.100" py={6}>
          <HStack justify="space-between" pr={10}>
             <VStack align="start" spacing={0}>
               <Text fontSize="2xl" fontWeight="bold">Trip Details</Text>
               <Text fontSize="sm" color="gray.500">#{trip?.id.slice(-12).toUpperCase()}</Text>
             </VStack>
             {trip && (
               <Badge colorScheme={trip.status === 'completed' ? 'green' : trip.status === 'scheduled' ? 'blue' : 'orange'} size="lg" px={4} py={1} borderRadius="full">
                 {trip.status.replace('_', ' ').toUpperCase()}
               </Badge>
             )}
          </HStack>
        </ModalHeader>
        <ModalCloseButton top={6} right={6} />
        <ModalBody p={8}>
          {isLoading ? (
            <Center py={20}><Spinner size="xl" color="brand.500" thickness="4px" /></Center>
          ) : trip ? (
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={10}>
              <VStack align="stretch" spacing={6}>
                {/* Route Information */}
                <Card variant="outline" borderRadius="2xl" borderStyle="dashed">
                  <CardBody>
                    <VStack align="start" spacing={4}>
                      <HStack color="brand.500">
                        <Route size={20} />
                        <Text fontWeight="bold" fontSize="lg">Route & Schedule</Text>
                      </HStack>
                      <VStack align="start" spacing={2} w="full">
                        <Box w="full" p={3} bg="brand.50" borderRadius="xl">
                          <Text fontWeight="bold" color="brand.800">{trip.route?.name}</Text>
                          <Text fontSize="sm" color="brand.600">{trip.route?.startPoint} → {trip.route?.endPoint}</Text>
                        </Box>
                        <SimpleGrid columns={2} w="full" spacing={4} pt={2}>
                           <VStack align="start" spacing={0}>
                             <Text fontSize="xs" color="gray.500">DEPARTURE</Text>
                             <Text fontWeight="semibold">{trip.scheduledDeparture ? new Date(trip.scheduledDeparture).toLocaleString() : 'N/A'}</Text>
                           </VStack>
                           <VStack align="start" spacing={0}>
                             <Text fontSize="xs" color="gray.500">ARRIVAL</Text>
                             <Text fontWeight="semibold">{trip.scheduledArrival ? new Date(trip.scheduledArrival).toLocaleString() : 'N/A'}</Text>
                           </VStack>
                        </SimpleGrid>
                      </VStack>
                    </VStack>
                  </CardBody>
                </Card>

                {/* Team Information */}
                <Card variant="outline" borderRadius="2xl">
                  <CardBody>
                    <HStack spacing={4} w="full">
                      <VStack align="start" spacing={4} flex={1}>
                        <HStack color="blue.500">
                          <Users size={20} />
                          <Text fontWeight="bold">Driver & Operator</Text>
                        </HStack>
                        
                        <HStack spacing={3}>
                          <Avatar size="sm" name={trip.daladala?.driver ? `${trip.daladala.driver.firstName} ${trip.daladala.driver.lastName}` : 'D'} bg="blue.100" color="blue.600" />
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm" fontWeight="bold">{trip.daladala?.driver ? `${trip.daladala.driver.firstName} ${trip.daladala.driver.lastName}` : 'Not Assigned'}</Text>
                            <Text fontSize="xs" color="gray.500">Driver · {trip.daladala?.driver?.phoneNumber || 'No phone'}</Text>
                          </VStack>
                        </HStack>

                        <HStack spacing={3}>
                          <Avatar size="sm" name={trip.daladala?.owner ? `${trip.daladala.owner.firstName} ${trip.daladala.owner.lastName}` : 'O'} bg="purple.100" color="purple.600" />
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm" fontWeight="bold">{trip.daladala?.owner ? `${trip.daladala.owner.firstName} ${trip.daladala.owner.lastName}` : 'Not Assigned'}</Text>
                            <Text fontSize="xs" color="gray.500">Operator · {trip.daladala?.owner?.phoneNumber || 'No phone'}</Text>
                          </VStack>
                        </HStack>
                      </VStack>
                      
                      <Box w="120px" p={4} bg="gray.50" borderRadius="2xl" border="1px solid" borderColor="gray.100" textAlign="center">
                          <Truck size={24} style={{ margin: '0 auto' }} color="var(--chakra-colors-blue-500)" />
                          <Text mt={2} fontSize="sm" fontWeight="bold">{trip.daladala?.registrationNumber}</Text>
                          <Text fontSize="xs" color="gray.500">{trip.daladala?.totalSeats} Seats</Text>
                      </Box>
                    </HStack>
                  </CardBody>
                </Card>
                
                {/* Bookings Summary */}
                <VStack align="start" spacing={2}>
                   <Text fontWeight="bold" fontSize="sm">Passengers ({trip.bookings?.length || 0})</Text>
                   <VStack align="stretch" spacing={2} maxH="220px" overflowY="auto" w="full" pr={2}>
                      {trip.bookings?.length === 0 ? (
                        <Text fontSize="sm" color="gray.400" fontStyle="italic">No bookings yet.</Text>
                      ) : trip.bookings?.map((b: any) => {
                        let passengerNameDisplay = 'Unknown';
                        if (b.passengerName) {
                          passengerNameDisplay = b.passengerName;
                        } else if (b.user?.firstName || b.user?.lastName) {
                          passengerNameDisplay = `${b.user.firstName || ''} ${b.user.lastName || ''}`.trim();
                        } else if (b.notes) {
                          try {
                            const notes = typeof b.notes === 'string' ? JSON.parse(b.notes) : b.notes;
                            passengerNameDisplay = notes.passengerName || 'Unknown';
                          } catch { /* ignore */ }
                        }
                        let seatDisplay = 'N/A';
                        try {
                          const seats = typeof b.seatNumbers === 'string' ? JSON.parse(b.seatNumbers) : (b.seatNumbers || []);
                          seatDisplay = Array.isArray(seats) && seats.length > 0 ? seats.join(', ') : 'N/A';
                        } catch { /* ignore */ }
                        return (
                          <HStack key={b.id} p={3} bg="gray.50" borderRadius="xl" justify="space-between">
                             <VStack align="start" spacing={0}>
                               <Text fontSize="sm" fontWeight="bold">{passengerNameDisplay}</Text>
                               <Text fontSize="xs" color="gray.500">Seat(s): {seatDisplay} · {b.boardingStop?.name || '?'} → {b.alightingStop?.name || '?'}</Text>
                             </VStack>
                             <Badge variant="subtle" colorScheme={b.paymentStatus === 'paid' ? 'green' : b.paymentStatus === 'failed' ? 'red' : 'orange'} fontSize="xs">{b.paymentStatus || 'pending'}</Badge>
                          </HStack>
                        );
                      })}
                   </VStack>
                </VStack>
              </VStack>

              {/* Seat Selection Map */}
              <VStack align="center" spacing={4}>
                <Text fontWeight="bold" fontSize="lg" w="full">Bus Seat Layout</Text>
                <BusSeatMap
                  capacity={trip.daladala?.totalSeats || 0}
                  layoutType={seatLayout.layoutType || '2+1'}
                  layoutRows={seatLayout.rows || 0}
                  lastRowFull={seatLayout.lastRowFull}
                  reservedSeats={seatLayout.reservedSeats || []}
                  bookedSeats={bookedSeats}
                />
                <Card bg="brand.50" p={2} variant="unstyled" w="full">
                  <SimpleGrid columns={3} spacing={2} textAlign="center">
                     <VStack spacing={0}><Text fontWeight="bold" color="brand.600">{Object.keys(bookedSeats).length}</Text><Text fontSize="xs">Booked</Text></VStack>
                     <VStack spacing={0}><Text fontWeight="bold" color="green.600">{Math.max(0, (trip.daladala?.totalSeats ?? 0) - Object.keys(bookedSeats).length)}</Text><Text fontSize="xs">Available</Text></VStack>
                     <VStack spacing={0}><Text fontWeight="bold">{trip.daladala?.totalSeats ?? '–'}</Text><Text fontSize="xs">Total</Text></VStack>
                  </SimpleGrid>
                </Card>
              </VStack>
            </SimpleGrid>
          ) : (
            <Center py={20}><Text color="gray.500">Trip not found.</Text></Center>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default function TripsPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [selectedDate, setSelectedDate] = useState('')
  const [selectedRoute, setSelectedRoute] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure()
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null)
  const toast = useToast()
  const queryClient = useQueryClient()

  const handleView = (tripId: string) => {
    setSelectedTripId(tripId)
    onViewOpen()
  }

  // Fetch real data from API
  const { data: tripsData, isLoading: tripsLoading } = useQuery(
    'trips',
    () => cmsService.getTrips({ limit: 100 }),
    {
      retry: false,
    }
  )

  const { data: dashboardStats } = useQuery(
    'dashboard-stats',
    () => cmsService.getDashboardStats(),
    {
      retry: false,
    }
  )

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, selectedStatus, selectedDate, selectedRoute])

  // Use real data only - no fallbacks
  const trips: Trip[] = tripsData?.data || []

  const stats = [
    { title: "Total Trips", value: trips.length, color: "blue", icon: Route },
    { title: "Scheduled", value: trips.filter(t => t.status === 'scheduled').length, color: "blue", icon: Calendar },
    { title: "In Progress", value: trips.filter(t => ['boarding', 'in_transit'].includes(t.status)).length, color: "orange", icon: Navigation },
    { title: "Completed", value: trips.filter(t => t.status === 'completed').length, color: "green", icon: CheckCircle },
    { title: "Cancelled", value: trips.filter(t => t.status === 'cancelled').length, color: "red", icon: XCircle },
  ]

  const filteredTrips = trips.filter((trip: Trip) => {
    const daladalaNumber = trip.daladala?.registrationNumber.toLowerCase() || ''
    const routeName = trip.route?.name.toLowerCase() || ''
    const driverName = trip.daladala?.driver ?
      `${trip.daladala.driver.firstName} ${trip.daladala.driver.lastName}`.toLowerCase() : ''

    const matchesSearch = daladalaNumber.includes(searchTerm.toLowerCase()) ||
      routeName.includes(searchTerm.toLowerCase()) ||
      driverName.includes(searchTerm.toLowerCase())

    const matchesStatus = selectedStatus === 'all' || trip.status === selectedStatus

    const matchesDate = !selectedDate ||
      (trip.scheduledDeparture && new Date(trip.scheduledDeparture).toDateString() === new Date(selectedDate).toDateString())

    const matchesRoute = selectedRoute === 'all' || trip.route?.id === selectedRoute

    return matchesSearch && matchesStatus && matchesDate && matchesRoute
  })

  // Derive unique routes from trips for the filter dropdown
  const uniqueRoutes = Array.from(
    new Map(
      trips
        .filter((t: Trip) => t.route && t.route.id)
        .map((t: Trip) => [t.route!.id, t.route])
    ).values()
  )

  // Pagination logic
  const totalPages = Math.ceil(filteredTrips.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentTrips = filteredTrips.slice(startIndex, endIndex)

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'blue'
      case 'boarding': return 'orange'
      case 'in_transit': return 'purple'
      case 'completed': return 'green'
      case 'cancelled': return 'red'
      default: return 'gray'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled': return Calendar
      case 'boarding': return Users
      case 'in_transit': return Navigation
      case 'completed': return CheckCircle
      case 'cancelled': return XCircle
      default: return AlertCircle
    }
  }

  const handleStatusUpdate = async (tripId: string, newStatus: string) => {
    try {
      await cmsService.updateTripStatus(tripId, newStatus)
      await queryClient.invalidateQueries('trips')
      await queryClient.invalidateQueries('dashboard-stats')
      toast({
        title: 'Status Updated',
        description: 'Trip status has been updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: getErrorMessage(error, 'Failed to update trip status.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  // Show loading state
  if (tripsLoading) {
    return (
      <Center h="64">
        <Spinner size="lg" color="brand.500" />
      </Center>
    )
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Flex align="center" justify="space-between" flexDirection={{ base: 'column', md: 'row' }} gap={4}>
        <VStack align={{ base: 'center', md: 'start' }} spacing={2}>
          <Heading size="xl" color="gray.900">
            Trip Management
          </Heading>
          <Text color="gray.600">
            Monitor and manage all daladala trips
          </Text>
        </VStack>
        <HStack spacing={3}>
          <Button
            leftIcon={<Download size={16} />}
            variant="outline"
            size="sm"
          >
            Export
          </Button>
          <Button
            leftIcon={<Plus size={16} />}
            colorScheme="brand"
            onClick={onOpen}
          >
            Create Trip
          </Button>
        </HStack>
      </Flex>

      {/* Stats Cards */}
      <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4}>
        {stats.map((stat, index) => (
          <Card key={index} bg="white" shadow="sm">
            <CardBody>
              <HStack justify="space-between">
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" color="gray.600" fontWeight="medium">
                    {stat.title}
                  </Text>
                  <Text fontSize="xl" fontWeight="bold" color="gray.900">
                    {stat.value}
                  </Text>
                </VStack>
                <Box
                  p={2}
                  borderRadius="lg"
                  bg={`${stat.color}.100`}
                  color={`${stat.color}.600`}
                >
                  <stat.icon size={20} />
                </Box>
              </HStack>
            </CardBody>
          </Card>
        ))}
      </SimpleGrid>

      {/* Filters and Search */}
      <Card bg="white" shadow="sm">
        <CardBody>
          <HStack spacing={4} flexDirection={{ base: 'column', md: 'row' }}>
            <InputGroup maxW="400px">
              <InputLeftElement pointerEvents="none">
                <Search size={16} color="var(--chakra-colors-gray-400)" />
              </InputLeftElement>
              <Input
                placeholder="Search trips by daladala, route, or driver..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Select
              maxW="200px"
              value={selectedRoute}
              onChange={(e) => setSelectedRoute(e.target.value)}
            >
              <option value="all">All Routes</option>
              {uniqueRoutes.map((route: any) => (
                <option key={route.id} value={route.id}>
                  {route.name}
                </option>
              ))}
            </Select>
            <Select
              maxW="200px"

              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="scheduled">Scheduled</option>
              <option value="boarding">Boarding</option>
              <option value="in_transit">In Transit</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </Select>
            <Input
              type="date"
              maxW="200px"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </HStack>
        </CardBody>
      </Card>

      {/* Trips Table */}
      <Card bg="white" shadow="sm">
        <CardHeader>
          <Flex justify="space-between" align="center">
            <Heading size="md" color="gray.900">
              Trips List
            </Heading>
            <Text color="gray.600" fontSize="sm">
              {filteredTrips.length} trip{filteredTrips.length !== 1 ? 's' : ''}
            </Text>
          </Flex>
        </CardHeader>
        <CardBody>
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Trip</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Daladala</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Route</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Driver</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Schedule</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Seats</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Status</Th>
                  <Th borderBottom="2px solid" borderColor="gray.200" py={4}>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredTrips.length === 0 ? (
                  <Tr>
                    <Td colSpan={8} py={12}>
                      <Center>
                        <VStack spacing={4}>
                          <Box p={4} bg="gray.100" borderRadius="full">
                            <Route size={48} color="var(--chakra-colors-gray-500)" />
                          </Box>
                          <VStack spacing={2}>
                            <Heading size="md" color="gray.900">
                              No trips found
                            </Heading>
                            <Text color="gray.600" textAlign="center">
                              {searchTerm || selectedStatus !== 'all' || selectedDate
                                ? 'No trips match your current filters.'
                                : 'Get started by creating your first trip.'
                              }
                            </Text>
                          </VStack>
                          {!searchTerm && selectedStatus === 'all' && !selectedDate && (
                            <Button
                              colorScheme="brand"
                              leftIcon={<Plus size={16} />}
                              onClick={onOpen}
                              borderRadius="xl"
                            >
                              Create Your First Trip
                            </Button>
                          )}
                        </VStack>
                      </Center>
                    </Td>
                  </Tr>
                ) : (
                  currentTrips.map((trip: Trip) => {
                    const StatusIcon = getStatusIcon(trip.status)
                    return (
                      <Tr key={trip.id} _hover={{ bg: 'gray.50' }} borderBottom="1px solid" borderColor="gray.100">
                        <Td py={4}>
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="medium" color="gray.900">
                              Trip #{trip.id.slice(-8)}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {trip.createdAt && !isNaN(new Date(trip.createdAt).getTime())
                                ? new Date(trip.createdAt).toLocaleDateString()
                                : 'N/A'
                              }
                            </Text>
                          </VStack>
                        </Td>
                        <Td py={4}>
                          <HStack spacing={2}>
                            <Truck size={14} color="var(--chakra-colors-blue-500)" />
                            <VStack align="start" spacing={0}>
                              <Text fontSize="sm" color="gray.900" fontWeight="medium">
                                {trip.daladala?.registrationNumber || 'N/A'}
                              </Text>
                              <Text fontSize="xs" color="gray.600">
                                {trip.daladala?.totalSeats || 0} seats
                              </Text>
                            </VStack>
                          </HStack>
                        </Td>
                        <Td py={4}>
                          <VStack align="start" spacing={1}>
                            <Text fontSize="sm" color="gray.900" fontWeight="medium">
                              {trip.route?.name || 'N/A'}
                            </Text>
                            <Text fontSize="xs" color="gray.600">
                              {trip.route?.startPoint || 'N/A'} → {trip.route?.endPoint || 'N/A'}
                            </Text>
                          </VStack>
                        </Td>
                        <Td py={4}>
                          {trip.daladala?.driver && (trip.daladala.driver.firstName || trip.daladala.driver.lastName) ? (
                            <VStack align="start" spacing={1}>
                              <Text fontSize="sm" color="gray.900">
                                {trip.daladala.driver.firstName || ''} {trip.daladala.driver.lastName || ''}
                              </Text>
                              {trip.daladala.driver.phoneNumber && (
                                <Text fontSize="xs" color="gray.600">
                                  {trip.daladala.driver.phoneNumber}
                                </Text>
                              )}
                            </VStack>
                          ) : (
                            <Text fontSize="sm" color="gray.500">No Driver</Text>
                          )}
                        </Td>
                        <Td py={4}>
                          <VStack align="start" spacing={1}>
                            <Text fontSize="sm" color="gray.900">
                              {trip.scheduledDeparture ? new Date(trip.scheduledDeparture).toLocaleString() : 'N/A'}
                            </Text>
                            {trip.actualDeparture && trip.actualDeparture !== trip.scheduledDeparture && (
                              <Text fontSize="xs" color="orange.600">
                                Actual: {new Date(trip.actualDeparture).toLocaleString()}
                              </Text>
                            )}
                          </VStack>
                        </Td>
                        <Td py={4}>
                          <VStack align="start" spacing={1}>
                            <HStack spacing={2}>
                              <Users size={12} color="var(--chakra-colors-blue-500)" />
                              <Text fontSize="sm" color="gray.900" fontWeight="medium">
                                {trip.totalBookings || 0} Booked
                              </Text>
                            </HStack>
                            <Text fontSize="xs" color="green.600">
                              {Math.max(0, (trip.daladala?.totalSeats ?? 0) - (trip.totalBookings || 0))} Available
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {trip.daladala?.totalSeats ?? '?'} Total
                            </Text>
                          </VStack>
                        </Td>
                        <Td py={4}>
                          <Badge
                            colorScheme={getStatusColor(trip.status)}
                            variant="subtle"
                            px={3}
                            py={1}
                            borderRadius="full"
                            textTransform="capitalize"
                          >
                            <HStack spacing={1}>
                              <StatusIcon size={12} />
                              <Text>{trip.status.replace('_', ' ')}</Text>
                            </HStack>
                          </Badge>
                        </Td>
                        <Td py={4}>
                          <HStack spacing={1}>
                            <Tooltip label="View Details">
                              <IconButton
                                aria-label="View"
                                icon={<Eye size={16} />}
                                size="sm"
                                variant="ghost"
                                colorScheme="blue"
                                onClick={() => handleView(trip.id)}
                              />
                            </Tooltip>
                            <Tooltip label="Update Status">
                              <Select
                                size="sm"
                                value={trip.status}
                                onChange={(e) => handleStatusUpdate(trip.id, e.target.value)}
                                w="130px"
                              >
                                <option value="scheduled">Scheduled</option>
                                <option value="boarding">Boarding</option>
                                <option value="in_transit">In Transit</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                              </Select>
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                    )
                  })
                )}
              </Tbody>
            </Table>
          </Box>
        </CardBody>

        {/* Pagination */}
        {totalPages > 1 && (
          <HStack justify="center" py={4} borderTop="1px solid" borderColor="gray.100">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              isDisabled={currentPage === 1}
            >
              Previous
            </Button>
            <Text fontSize="sm" color="gray.600" px={4}>
              Page {currentPage} of {totalPages}
            </Text>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              isDisabled={currentPage === totalPages}
            >
              Next
            </Button>
          </HStack>
        )}
      </Card>

      {/* Create Trip Modal */}
      <TripFormModal isOpen={isOpen} onClose={onClose} />
        {/* View Modal */}
        <TripViewModal
          isOpen={isViewOpen}
          onClose={onViewClose}
          tripId={selectedTripId}
        />
    </VStack>
  )
}
